import { useEffect, useRef } from 'react'
import type { JsonObject } from '../../utils/json-pointer'
import { warn } from '../../../../shared/helpers'
import type { DataContextProviderProps as ProviderProps } from '../../DataContext/Provider'
import DataContextProvider from '../../DataContext/Provider'
import type { FormElementProps as FormElementProps } from '../Element'
import FormElement from '../Element'
import type { ContextState } from '../../DataContext'

export type FormHandlerProps = FormElementProps & {
  /**
   * Will enable autoComplete for all nested Field.String fields
   */
  autoComplete?: boolean

  /**
   * Will decouple the form element from rendering
   */
  decoupleForm?: boolean
}

type AllowedProviderContextProps = ProviderProps<JsonObject> &
  Pick<FormHandlerProps, 'decoupleForm' | 'autoComplete' | 'disabled'> &
  Pick<ContextState, 'restHandlerProps' | 'hasElementRef'>

const allowedProviderContextProps: Array<
  keyof AllowedProviderContextProps
> = [
  'id',
  'defaultData',
  'data',
  'schema',
  'ajvInstance',
  'errorMessages',
  'globalStatusId',
  'transformIn',
  'transformOut',
  'onChange',
  'onPathChange',
  'onSubmit',
  'onSubmitRequest',
  'onSubmitComplete',
  'onClear',
  'minimumAsyncBehaviorTime',
  'asyncSubmitTimeout',
  'scrollTopOnSubmit',
  'sessionStorageId',
  'locale',
  'countryCode',
  'translations',
  'translationsLoader',
  'messageFormatter',
  'autoComplete',
  'disabled',
  'required',
  'decoupleForm',
  'restHandlerProps',
]

export default function FormHandler<Data extends JsonObject>(
  props: ProviderProps<Data> &
    Omit<FormHandlerProps, keyof ProviderProps<Data>>
) {
  const { decoupleForm, children } = props

  const hasElementRef = useRef(false)
  useEffect(() => {
    if (decoupleForm && !hasElementRef.current) {
      warn('Please include a Form.Element when using decoupleForm!')
    }
  }, [decoupleForm])

  const providerProps = {
    hasElementRef,
    restHandlerProps: {},
  } as AllowedProviderContextProps

  for (const key in props) {
    if (
      allowedProviderContextProps.includes(
        key as keyof AllowedProviderContextProps
      )
    ) {
      providerProps[key] = props[key]
    } else if (key !== 'children') {
      providerProps.restHandlerProps[key] = props[key]
    }
  }

  return (
    <DataContextProvider {...providerProps}>
      {decoupleForm ? children : <FormElement>{children}</FormElement>}
    </DataContextProvider>
  )
}
